"use client"

import React, {useEffect, useRef} from "react";
import {Box, Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery,} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import FaIcon from "@/components/utilities/fa-icon";
import {LayoutDialogProps} from "@/types/layout";

export default function LayoutDialog({
                                         title,
                                         children,
                                         handleClose,
                                         open,
                                         dialogActions,
                                         headerBorder,
                                         hasSeparatedActions,
                                         haveCloseBtn = true,
                                         contentSx,
                                         contentRef,
                                         sx,
                                         maxWidth = 'md',
                                         fullWidth = true,
                                         backgroundColor,
                                         headerLeftContent,
                                         outOfContent,
                                         disableFullScrollBlock = true,
                                         autoFocusFirstField = true,
                                         guideBlocker = false,
                                         ...otherProps
                                     }: LayoutDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const dialogRootRef = useRef<HTMLDivElement | null>(null);
    const contentRootRef = useRef<HTMLDivElement | null>(null);

    const focusFirstField = () => {
        const root = contentRootRef.current;
        if (!root) return;

        const el =
            (root.querySelector("textarea") as HTMLTextAreaElement | null) ||
            (root.querySelector('input:not([type="hidden"])') as HTMLInputElement | null);

        if (!el) return;

        requestAnimationFrame(() => {
            el.focus();

            const canSelect =
                el instanceof HTMLTextAreaElement ||
                (el instanceof HTMLInputElement &&
                    ["text", "search", "tel", "url", "password"].includes((el.type || "text").toLowerCase()));

            if (!canSelect) return;

            try {
                const end = el.value?.length ?? 0;
                el.setSelectionRange(end, end);
            } catch {
                console.log("")
            }
        });
    };


    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const isCtrlEnter = (e.ctrlKey || e.metaKey) && e.key === "Enter";
            if (!isCtrlEnter) return;

            e.preventDefault();

            const root = dialogRootRef.current;
            if (!root) return;

            let btn = root.querySelector(
                'button[data-dialog-primary-action]:not([disabled])'
            ) as HTMLButtonElement | null;

            if (!btn) {
                btn =
                    (root.querySelector(
                        'button.MuiButton-root[type="submit"]:not([disabled])'
                    ) as HTMLButtonElement | null) ||
                    (root.querySelector(
                        'button.MuiButton-root.MuiButton-contained:not([disabled])'
                    ) as HTMLButtonElement | null);
            }


            btn?.click();
        };

        document.addEventListener("keydown", handleKeyDown, true);
        return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, [open]);


    useEffect(() => {
        if (!open || disableFullScrollBlock) return;
        const scrollY = window.scrollY;
        const lockScroll = () => window.scrollTo(0, scrollY);
        const prevent = (e: Event) => e.preventDefault();
        const preventKeys = (e: KeyboardEvent) => {
            if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"].includes(e.key)) {
                e.preventDefault();
            }
        };

        window.addEventListener("scroll", lockScroll);
        window.addEventListener("wheel", prevent, {passive: false});
        window.addEventListener("touchmove", prevent, {passive: false});
        window.addEventListener("keydown", preventKeys);

        return () => {
            window.removeEventListener("scroll", lockScroll);
            window.removeEventListener("wheel", prevent);
            window.removeEventListener("touchmove", prevent);
            window.removeEventListener("keydown", preventKeys);
        };
    }, [open]);

    return (
        <Dialog
            data-guide-blocker = {guideBlocker ? "true" : undefined}
            TransitionProps = {{
                onEntered: () => {
                    if (!autoFocusFirstField) return;
                    requestAnimationFrame(() => focusFirstField());
                },
            }}

            PaperProps = {{
                ref: (node: HTMLDivElement | null) => {
                    dialogRootRef.current = node;
                },
            }}

            maxWidth = {maxWidth}
            fullWidth = {fullWidth}
            fullScreen = {fullScreen}
            scroll="paper"
            open = {open}
            onClose = {(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    handleClose(event);
                }
            }}
            sx = {{
                px: {xs: 0, sm: 2, md: 4},
                ...sx,
            }}
            {...otherProps}
        >
            {/* Header */}
            {title && (
                <Box
                    display = "flex"
                    alignItems = "center"
                    justifyContent = "center"
                    position = "relative"
                    width = "100%"
                    borderBottom = {headerBorder ? 1 : 0}
                    boxShadow = {theme.customShadows.card}
                    borderColor = "divider"
                >
                    <DialogTitle
                        fontSize = {18}
                        fontWeight = {400}
                        textAlign = "center"
                        sx = {{
                            maxWidth: "80%",
                            px: 2,
                            py: 2.5,
                        }}
                    >
                        {title}
                    </DialogTitle >

                    {headerLeftContent && (
                        <Box
                            display = "flex"
                            alignItems = "center"
                            position = "absolute"
                            height = "100%"
                            top = {0}
                            left = {0}
                            px = {2}
                        >
                            {headerLeftContent}
                        </Box >
                    )}
                </Box >
            )}

            {outOfContent}

            <DialogContent
                ref = {(node: HTMLDivElement | null) => {
                    contentRootRef.current = node;
                    if (typeof contentRef === "function") contentRef(node)

                    else if (contentRef && typeof contentRef === "object") {
                        (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                    }
                }}
                sx={{
                    px: 4,
                    pb: {md: 4, xs: 1},
                    pt: 4,
                    bgcolor: "background.paper",
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    ...contentSx,
                }}
            >
                {children}

                {dialogActions && (
                    <Box mt = {2} pt = {2} >
                        {dialogActions}
                    </Box >
                )}

                {open && haveCloseBtn && (
                    <IconButton
                        onClick = {handleClose}
                        sx = {{
                            zIndex: 100,
                            position: "absolute",
                            top: 18,
                            right: 12,
                            p: 1,
                            color: theme.palette.grey[600],
                            "&:hover": {
                                backgroundColor: "grey.100",
                            },
                        }}
                    >
                        <FaIcon fontSize = {{md: 22, xs: 20}} variant = "solid" faIcon = "fa-close" />
                    </IconButton >
                )}
            </DialogContent >
        </Dialog >
    );
}
